import type { CarrierRequestView } from "@agency/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Bot, Download, FlaskConical, Send } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { callAction } from "../api";
import { useToast } from "../components/Toast";
import { Badge, Button, Card, CardHeader, ErrorState, Input, LoadingState, cn } from "../components/ui";
import { useLocale, useT } from "../i18n";
import { money, shortDate } from "../theme";
import { RequestStatusBadge } from "./Carriers";

export function CarrierRequestScreen() {
  const { requestId = "" } = useParams();
  const t = useT();
  const { locale } = useLocale();
  const qc = useQueryClient();
  const { toast } = useToast();
  const key = ["carrier-request", requestId];
  const q = useQuery({
    queryKey: key,
    queryFn: async () => (await callAction<"agency.carrier.request.get", CarrierRequestView>("agency.carrier.request.get", { requestId })).result,
    refetchInterval: (query) => { const s = query.state.data?.request.status; return s === "downloading" || s === "processing" || s === "submitted" ? 2500 : false; },
  });
  const [answer, setAnswer] = useState("");
  const invalidate = () => { qc.invalidateQueries({ queryKey: key }); qc.invalidateQueries({ queryKey: ["carriers"] }); qc.invalidateQueries({ queryKey: ["today"] }); if (q.data) qc.invalidateQueries({ queryKey: ["customer", q.data.request.customerId] }); };
  const err = (e: unknown) => toast({ title: t("common.error"), body: (e as Error).message, tone: "error" });

  const submit = useMutation({ mutationFn: () => callAction<"agency.carrier.request.submit", CarrierRequestView>("agency.carrier.request.submit", { requestId }), onSuccess: invalidate, onError: err });
  const reply = useMutation({
    mutationFn: (questionId: string) => callAction<"agency.carrier.request.answer", CarrierRequestView>("agency.carrier.request.answer", { requestId, questionId, answer }),
    onSuccess: () => { setAnswer(""); invalidate(); toast({ title: t("carriers.request.answered"), tone: "success" }); },
    onError: err,
  });
  const fetchFile = useMutation({
    mutationFn: (via: "direct" | "executor") => callAction<"agency.carrier.request.fetch", CarrierRequestView>("agency.carrier.request.fetch", { requestId, via }),
    onSuccess: (r, via) => { invalidate(); toast({ title: via === "direct" ? t("carriers.request.fetched") : t("carriers.request.queued"), body: r.result.importBatch ? `${r.result.importBatch.filename} · ${r.result.importBatch.status}` : undefined, tone: "success" }); },
    onError: err,
  });

  if (q.isLoading) return <LoadingState />;
  if (q.isError || !q.data) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const d = q.data;
  const r = d.request;
  const open = r.questions.find((x) => !x.answeredAt);
  const batch = d.importBatch;

  return (
    <div className="space-y-4">
      {r.simulated ? <div className="rounded-2xl border border-accent bg-accent/15 px-4 py-3 text-sm font-semibold text-amber-900 flex items-center gap-2"><FlaskConical className="h-4 w-4 shrink-0" />{t("carriers.request.banner")}</div> : null}
      <div className="flex flex-wrap items-center gap-2">
        <div className="mr-auto">
          <p className="text-sm text-ink/60"><Link to="/carriers" className="hover:underline">{t("carriers.title")}</Link> · {r.carrierName}</p>
          <h1 className="text-2xl font-bold">{t("carriers.request.title")} · {t(`carriers.kind.${r.kind}`)}</h1>
        </div>
        {r.simulated ? <Badge tone="amber">{t("carriers.simulated")}</Badge> : null}
        <RequestStatusBadge status={r.status} />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="md:col-span-2">
          <CardHeader title={t("carriers.request.timeline")} />
          <ol className="px-5 pb-5 space-y-2">
            {r.timeline.map((e, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <span className={cn("mt-1.5 h-2.5 w-2.5 rounded-full shrink-0", e.simulated ? "bg-accent" : "bg-primary")} />
                <div>
                  <p className="font-semibold">{e.event} {e.simulated ? <Badge tone="amber" className="ml-1">{t("carriers.simulated")}</Badge> : null}</p>
                  {e.detail ? <p className="text-ink/70">{e.detail}</p> : null}
                  <p className="text-xs text-ink/50">{shortDate(e.at, locale)} {new Date(e.at).toLocaleTimeString(locale === "es" ? "es-MX" : "en-US", { hour: "numeric", minute: "2-digit" })}</p>
                </div>
              </li>
            ))}
          </ol>

          {r.status === "draft" ? (
            <div className="px-5 pb-5">
              {d.carrier.simulated ? <Button onClick={() => submit.mutate()} loading={submit.isPending}><Send className="h-4 w-4" />{t("carriers.request.submit")}</Button> : <p className="text-sm text-amber-800">{t("carriers.request.notVerified")}</p>}
            </div>
          ) : null}

          {open ? (
            <form className="mx-5 mb-5 rounded-2xl border border-accent/60 bg-accent/10 p-4 space-y-2" onSubmit={(e: FormEvent) => { e.preventDefault(); if (answer.trim()) reply.mutate(open.id); }}>
              <p className="text-xs font-bold uppercase tracking-wide text-amber-900">{t("carriers.request.question")} · {t("carriers.simulated")}</p>
              <p className="font-semibold">{locale === "es" && open.promptEs ? open.promptEs : open.prompt}</p>
              <div className="flex gap-2">
                <Input className="flex-1" placeholder={t("carriers.request.answerPlaceholder")} value={answer} onChange={(e) => setAnswer(e.target.value)} />
                <Button type="submit" disabled={!answer.trim()} loading={reply.isPending}>{t("carriers.request.answer")}</Button>
              </div>
            </form>
          ) : null}

          {r.status === "file_ready" ? (
            <div className="px-5 pb-5 flex flex-wrap gap-2">
              <Button onClick={() => fetchFile.mutate("direct")} loading={fetchFile.isPending}><Download className="h-4 w-4" />{t("carriers.request.fetchDirect")}</Button>
              <Button variant="outline" onClick={() => fetchFile.mutate("executor")} loading={fetchFile.isPending}><Bot className="h-4 w-4" />{t("carriers.request.fetchExecutor")}</Button>
            </div>
          ) : null}
          {r.status === "imported" ? <p className="px-5 pb-5 text-sm text-primary-dark font-semibold">{t("carriers.request.followUp")}</p> : null}
        </Card>

        <div className="space-y-4">
          <Card>
            <CardHeader title={t("carriers.request.customer")} />
            <div className="px-5 pb-5 text-sm space-y-1">
              <Link to={`/customers/${r.customerId}`} className="font-semibold hover:underline">{d.customerName} →</Link>
              {d.policy ? <p className="text-ink/70">{t("carriers.request.policy")}: {d.policy.carrier} · {d.policy.line.replace(/_/g, " ")} · <span className="font-mono text-xs">{d.policy.policyNumber}</span> · {money(d.policy.premiumCents)}</p> : null}
            </div>
          </Card>
          {d.resultDocument ? (
            <Card>
              <CardHeader title={t("carriers.request.file")} action={<Badge tone="amber">{t("carriers.simulated")}</Badge>} />
              <div className="px-5 pb-5 text-sm space-y-2">
                <p className="truncate font-mono text-xs">{d.resultDocument.title}</p>
                {d.downloadUrl ? <a href={d.downloadUrl} target="_blank" rel="noopener" className="inline-flex min-h-[40px] items-center gap-2 rounded-xl border border-ink/15 px-3 font-semibold hover:bg-ink/5"><Download className="h-4 w-4" />{t("carriers.request.download")}</a> : null}
              </div>
            </Card>
          ) : null}
          {batch ? (
            <Card>
              <CardHeader title={t("carriers.request.import")} />
              <div className="px-5 pb-5 text-sm space-y-1">
                <p><Badge tone={batch.status === "imported" ? "green" : "blue"}>{batch.status}</Badge> <span className="text-ink/60">{batch.rowCount} rows</span></p>
                {batch.result ? <p className="text-ink/70">{batch.result.created.policies + batch.result.created.customers} created · {batch.result.updated.policies + batch.result.updated.customers} updated · {batch.result.exceptions} exceptions</p> : batch.validation ? <p className="text-ink/70">{batch.validation.creates.policies} to create · {batch.validation.updates.policies} to update · {batch.validation.exceptions} exceptions</p> : null}
                <Link to={`/import/${batch.id}`} className="font-semibold text-primary-dark hover:underline">{t("carriers.request.openBatch")} →</Link>
                {batch.result?.policyIds.length ? <p className="text-xs text-ink/55">{batch.result.policyIds.length} {t("common.policies").toLowerCase()}</p> : null}
              </div>
            </Card>
          ) : null}
          {d.job ? (
            <Card>
              <CardHeader title={t("carriers.request.job")} action={<Badge tone={d.job.status === "completed" ? "green" : d.job.status === "failed" ? "red" : "blue"}>{d.job.status}</Badge>} />
              <ul className="px-5 pb-5 text-xs text-ink/70 space-y-1">
                {d.job.log.slice(-5).map((l, i) => <li key={i}>{l.message}</li>)}
              </ul>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}
